import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  isPromoDefinitionHiddenReason,
  isPromoNotActiveReason,
  isPromoNotFoundReason,
  isPromoRedeemedReason,
} from '@/utils/promo-inactive-reason';
import { formatPromoDiscount } from './formatPromoDiscount';
import styles from './PromoCodeFooterLabel.module.css';

const TOOLTIP_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 420;
const TOOLTIP_VERTICAL_GAP = 12;

type TooltipPosition = {
  top: number;
  left: number;
  maxWidth: number;
  shiftX: number;
};

export type PromoCodeInfo = {
  code: string;
  status: 'active' | 'inactive';
  type?: 'fixed' | 'percentage';
  value?: number;
  inactiveReason?: string;
  minOrderAmount?: number | null;
  restrictedDomainNames?: string[];
  domainRestrictionPartial?: boolean;
  domainRestrictionInvalid?: boolean;
};

type PromoCodeFooterLabelProps = {
  promoCode?: string;
  promoCodeInfo?: PromoCodeInfo;
  pendingCode?: string;
};

function resolveDisplayCode(
  promoCode?: string,
  pendingCode?: string,
  info?: PromoCodeInfo
): string | undefined {
  const candidates = [promoCode, pendingCode, info?.code];
  for (const c of candidates) {
    const t = c?.trim();
    if (t) return t;
  }
  return undefined;
}


function isPromoInvalid(info: PromoCodeInfo | undefined): boolean {
  if (!info) return false;
  if (info.status === 'active') return false;

  const reason = info.inactiveReason;
  if (!reason) return false;

  if (isPromoNotFoundReason(reason)) return true;
  if (isPromoRedeemedReason(reason)) return true;
  if (isPromoNotActiveReason(reason)) return true;
  return false;
}

function resolvePromoColor(info: PromoCodeInfo | undefined): '' | 'green' | 'yellow' {
  if (!info) return '';
  if (info.status === 'active' && info.domainRestrictionPartial) return 'yellow';
  if (info.status === 'active') return 'green';
  return '';
}

function shouldShowDiscountLine(info: PromoCodeInfo | undefined, discount: string): boolean {
  if (!discount || !info) return false;
  if (info.status === 'active') return true;
  if (info.status !== 'inactive') return false;
  if (isPromoDefinitionHiddenReason(info.inactiveReason)) return false;
  return Boolean(info.type != null && info.value != null);
}

function buildTooltip(info: PromoCodeInfo | undefined): string | undefined {
  if (!info) return undefined;
  const segments: string[] = [];
  if (info.inactiveReason) {
    segments.push(info.inactiveReason);
    return segments.join('\n');
  }
  if (info.restrictedDomainNames?.length) {
    segments.push(`Valid domains: ${info.restrictedDomainNames.join(', ')}`);
  }
  if (info.minOrderAmount != null && info.minOrderAmount > 0) {
    segments.push(`Minimum order: €${String(info.minOrderAmount)}`);
  }
  if (info.domainRestrictionPartial) {
    segments.push('Applied only to eligible ski passes.');
  }
  return segments.length ? segments.join('\n') : undefined;
}

function calcTooltipPosition(anchor: HTMLElement): TooltipPosition {
  const maxWidth = Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - TOOLTIP_MARGIN * 2);
  const rect = anchor.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const top = rect.top - TOOLTIP_VERTICAL_GAP;
  const left = centerX;

  return { top, left, maxWidth, shiftX: 0 };
}

function calcTooltipShiftX(centerX: number, tooltipWidth: number): number {
  const minCenter = TOOLTIP_MARGIN + tooltipWidth / 2;
  const maxCenter = window.innerWidth - TOOLTIP_MARGIN - tooltipWidth / 2;

  if (centerX < minCenter) {
    return minCenter - centerX;
  }

  if (centerX > maxCenter) {
    return maxCenter - centerX;
  }

  return 0;
}

export function PromoCodeFooterLabel({
  promoCode,
  promoCodeInfo,
  pendingCode,
}: PromoCodeFooterLabelProps) {
  const tooltipId = useId();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const displayCode = resolveDisplayCode(promoCode, pendingCode, promoCodeInfo);
  const shouldRenderLabel = Boolean(displayCode) && !isPromoInvalid(promoCodeInfo);

  const discount = formatPromoDiscount(promoCodeInfo?.type, promoCodeInfo?.value);
  const showDiscountLine = shouldShowDiscountLine(promoCodeInfo, discount);
  const tooltip = buildTooltip(promoCodeInfo);
  const tooltipLines = tooltip?.split('\n').filter(Boolean) ?? [];
  const isActivePricing = promoCodeInfo?.status === 'active';
  const canShowTooltip = tooltipLines.length > 0;
  const promoColor = resolvePromoColor(promoCodeInfo);

  const openTooltip = useCallback(() => {
    if (!canShowTooltip) return;
    const anchor = rowRef.current ?? triggerRef.current;
    if (!anchor) return;
    setTooltipPosition(calcTooltipPosition(anchor));
    setIsTooltipOpen(true);
  }, [canShowTooltip]);

  const closeTooltip = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const handleTriggerMouseLeave = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && tooltipRef.current?.contains(next)) return;
    closeTooltip();
  }, [closeTooltip]);

  const handleTooltipMouseLeave = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && triggerRef.current?.contains(next)) return;
    closeTooltip();
  }, [closeTooltip]);

  const toggleTooltip = useCallback(() => {
    setIsTooltipOpen((isOpen) => {
      const anchor = rowRef.current ?? triggerRef.current;
      if (!isOpen && anchor && canShowTooltip) {
        setTooltipPosition(calcTooltipPosition(anchor));
      }
      return !isOpen;
    });
  }, [canShowTooltip]);

  useEffect(() => {
    if (!isTooltipOpen || !canShowTooltip) return undefined;

    const handlePositionUpdate = () => {
      const anchor = rowRef.current ?? triggerRef.current;
      if (anchor) {
        const next = calcTooltipPosition(anchor);
        const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width ?? next.maxWidth;
        const shiftX = calcTooltipShiftX(next.left, tooltipWidth);
        setTooltipPosition({ ...next, shiftX });
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTooltipOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (tooltipRef.current?.contains(target)) return;
      setIsTooltipOpen(false);
    };

    handlePositionUpdate();
    const rafId = window.requestAnimationFrame(handlePositionUpdate);
    window.addEventListener('resize', handlePositionUpdate);
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handlePositionUpdate);
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [canShowTooltip, isTooltipOpen]);

  if (!shouldRenderLabel) return null;

  return (
    <div className={styles.promoContainer} data-color={promoColor}>
      <div ref={rowRef} className={styles.promoRow}>
        {showDiscountLine && (
          <span
            className={styles.promoDiscount}
            data-muted={(!isActivePricing).toString()}
          >
            {discount} with
          </span>
        )}
        <span className={styles.promoCode}>{displayCode}</span>
        {(tooltip || isActivePricing) && canShowTooltip && (
          <span className={styles.promoInfoWrap}>
            {canShowTooltip ? (
              <button
                ref={triggerRef}
                type="button"
                className={styles.promoInfoButton}
                aria-expanded={isTooltipOpen}
                aria-describedby={isTooltipOpen ? tooltipId : undefined}
                aria-label="Show promo code details"
                onMouseEnter={openTooltip}
                onMouseLeave={handleTriggerMouseLeave}
                onClick={toggleTooltip}
              >
                <span className={styles.promoInfoIcon} aria-hidden>
                  ?
                </span>
              </button>
            ) : (
              <span className={styles.promoInfoIcon} aria-hidden>
                ?
              </span>
            )}
            {canShowTooltip && isTooltipOpen && tooltipPosition != null && createPortal(
              <div
                ref={tooltipRef}
                id={tooltipId}
                role="tooltip"
                className={styles.tooltipBalloon}
                onMouseLeave={handleTooltipMouseLeave}
                style={{
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                  maxWidth: tooltipPosition.maxWidth,
                  ['--tooltip-shift-x' as string]: `${tooltipPosition.shiftX}px`,
                  ['--tooltip-arrow-shift-x' as string]: `${tooltipPosition.shiftX}px`,
                }}
              >
                <button
                  type="button"
                  className={styles.tooltipClose}
                  aria-label="Close promo details"
                  onClick={closeTooltip}
                >
                  ×
                </button>
                <div className={styles.tooltipContent}>
                  {tooltipLines.map((line) => (
                    <p key={line} className={styles.tooltipLine}>{line}</p>
                  ))}
                </div>
              </div>,
              document.body,
            )}
          </span>
        )}
      </div>
    </div>
  );
}
